import { Controller } from 'react-hook-form'
import DatePicker from 'react-datepicker'
import { forwardRef } from 'react'
import { registerLocale } from 'react-datepicker'
import fr from 'date-fns/locale/fr'
import 'react-datepicker/dist/react-datepicker.css'
import './DatePickerStyles.css'
import { DateTime } from 'luxon'

// Enregistrer la locale française
registerLocale('fr', fr)

// Convertit une chaîne de date ou un objet Date en objet Date
const parseDateTime = (dateTimeValue) => {
  if (!dateTimeValue) return null
  
  if (typeof dateTimeValue === 'string') {
    // Utiliser Luxon pour créer une date avec le fuseau horaire local
    const luxonDate = DateTime.fromISO(dateTimeValue, { zone: 'local' })
    
    // Convertir en objet Date JavaScript pour react-datepicker
    return luxonDate.isValid ? luxonDate.toJSDate() : null
  }
  
  return dateTimeValue
}

// Formate une date pour l'affichage dans le format français
const formatDisplayDate = (date) => {
  if (!date) return ''
  
  // Convertir en DateTime Luxon puis formater
  const luxonDate = DateTime.fromJSDate(date)
  return luxonDate.isValid ? luxonDate.toFormat('dd/MM/yyyy HH:mm') : ''
}

export default ({
  name,
  control,
  label,
  defaultValue = '',
  disabled = false,
  rules = {},
  validator = {},
  className = '',
}) => {
  // Composant personnalisé pour le DatePicker qui respecte le style DaisyUI
  const CustomInput = forwardRef(({ value, onClick, onChange, disabled, date }, ref) => {
    // Si une date est fournie, l'utiliser pour l'affichage, sinon utiliser la valeur
    const displayValue = date ? formatDisplayDate(date) : (value || '')
    
    return (
      <input
        className="w-full"
        value={displayValue}
        onClick={onClick}
        onChange={onChange}
        ref={ref}
        disabled={disabled}
        // Suppression de readOnly pour permettre la saisie au clavier
      />
    )
  })
  
  CustomInput.displayName = 'CustomDateTimeInput'

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue}
      render={({ field }) => {
        // Conversion de la valeur en objet Date
        const dateValue = parseDateTime(field.value)
        
        return (
          <>
            <label className={`input w-auto ${className} ${validator.hasError && validator.hasError(name) ? 'validator' : ''}`}
              {...(validator.hasError && validator.hasError(name) ? {'aria-invalid': true} : {})}
            >
              <span className="label">{label}</span>
              <DatePicker
                selected={dateValue}
                onChange={(date) => {
                  if (!date) {
                    field.onChange('')
                    return
                  }
                  
                  // Utiliser Luxon pour formater la date avec l'heure
                  const luxonDate = DateTime.fromJSDate(date)
                  const formattedDate = luxonDate.toFormat("yyyy-MM-dd'T'HH:mm")
                  field.onChange(formattedDate)
                }}
                onBlur={field.onBlur}
                customInput={<CustomInput disabled={disabled} date={dateValue} />}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="dd/MM/yyyy HH:mm"
                disabled={disabled}
                className="w-full"
                locale="fr"
                placeholderText="Sélectionner une date et heure"
                timeCaption="Heure"
                shouldCloseOnSelect={false}
                disabledKeyboardNavigation={false}
                allowSameDay={true}
                strictParsing={false}
                isClearable={true}
                // Utiliser le fuseau horaire de Paris
                timeZone="Europe/Paris"
              />
            </label>
            {validator.getErrorMessage && (
              <div className="validator-hint hidden mt-0 empty:py-[0.6rem]">
                {validator.getErrorMessage(name)}
              </div>
            )}
          </>
        )
      }}
    />
  )
}